import { ComponentProps, ElementType, MouseEvent, useMemo } from "react";
import { twMerge } from "tailwind-merge";
import Spinner from "./Spinner";

type Color = "primary" | "danger" | "dark";
type Variant = "text" | "solid" | "outline";

type ButtonBaseProps = {
  color?: Color;
  variant?: Variant;
  loading?: boolean;
};

type ButtonProps<E extends ElementType> = {
  as?: E;
} & ButtonBaseProps &
  Omit<ComponentProps<E>, keyof ButtonBaseProps>;

type Colors = {
  [key in Color]?: {
    [key in Variant]?: string;
  };
};

export default function Button<E extends ElementType = "button">({
  as,
  color = "primary",
  variant = "text",
  loading = false,
  className,
  children,
  onClick,
  disabled,
  ...props
}: ButtonProps<E>) {
  const Component = as || "button";
  const createRipple = (e: MouseEvent<HTMLButtonElement>) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const left = e.clientX - rect.left;
    const top = e.clientY - rect.top;
    const ripple = document.createElement("span");
    ripple.className = [
      "absolute",
      "size-5",
      "rounded-full",
      "pointer-events-none",
      "scale-100 opacity-20",
      "transition-[opacity,transform]",
      "duration-700",
      "bg-current",
    ].join(" ");
    ripple.style.setProperty("left", `calc(${left}px - 0.625rem)`);
    ripple.style.setProperty("top", `calc(${top}px - 0.625rem)`);
    el.appendChild(ripple);
    setTimeout(() => {
      ripple.classList.replace("scale-100", "scale-[35]");
      ripple.classList.replace("opacity-20", "opacity-0");
    }, 0);
    setTimeout(() => {
      el.removeChild(ripple);
    }, 900);
  };

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    createRipple(e);
    onClick?.(e);
  };
  const handleColorClasses = useMemo(() => {
    const colors: Colors = {
      primary: {
        solid: "bg-primary text-white border-primary",
        outline: "bg-transparent text-primary border-primary",
        text: "bg-transparent text-primary border-transparent",
      },
      danger: {
        solid: "bg-danger text-white border-danger",
        outline: "bg-transparent text-danger border-danger",
        text: "bg-transparent text-danger border-transparent",
      },
      dark: {
        solid: "bg-dark text-white border-dark",
        outline: "bg-transparent text-dark border-dark",
        text: "bg-transparent text-dark border-transparent",
      },
    };
    if (!color) return null;
    return colors?.[color]?.[variant];
  }, [variant, color]);
  return (
    <Component
      onClick={handleClick}
      className={twMerge(
        "inline-block font-medium no-underline align-middle transition-all text-center rounded border px-3 py-1.5 text-base overflow-hidden relative disabled:opacity-75",
        handleColorClasses,
        className
      )}
      disabled={loading || disabled}
      {...props}
    >
      {children}
      {loading && (
        <span className="absolute inset-0 size-full flex items-center justify-center bg-transparent backdrop-blur p-1.5">
          <Spinner className="size-auto h-full aspect-square" />
        </span>
      )}
    </Component>
  );
}
